"use client";
// LEGACY UI: replace with Tooltip from @workspace/frontend-ui.
// Don't import this in new code; legacy-ui.json tracks the remaining importers
// and only shrinks (scripts/legacy-ui-check.mjs).

import classNames from "@casino/external/classNames";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type Position =
  | "top-center"
  | "top-start"
  | "top-end"
  | "bottom-center"
  | "bottom-start"
  | "bottom-end"
  | "left-center"
  | "right-center";

type Coords = { top: number; left: number };

type TooltipContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  position: Position;
  triggerRef: React.RefObject<HTMLDivElement | null>;
};

const TooltipCtx = createContext<TooltipContextValue>({
  open: false,
  setOpen: () => {},
  position: "top-center",
  triggerRef: { current: null },
});

type RootProps = { children: React.ReactNode };

type RootPropsWithPosition = RootProps & {
  position?: Position;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const TooltipRoot = ({
  children,
  position = "top-center",
  open: openProp,
  onOpenChange,
}: RootPropsWithPosition) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const triggerRef = useRef<HTMLDivElement>(null);
  return (
    <TooltipCtx.Provider value={{ open, setOpen, position, triggerRef }}>
      <div className="relative inline-block">{children}</div>
    </TooltipCtx.Provider>
  );
};

type TriggerProps = { children: React.ReactNode; className?: string };

const Trigger = ({ children, className }: TriggerProps) => {
  const { setOpen, triggerRef } = useContext(TooltipCtx);
  return (
    <div
      ref={triggerRef}
      className={classNames("inline-block", className)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {children}
    </div>
  );
};

function useTooltipCoords(
  position: Position,
  triggerRef: React.RefObject<HTMLDivElement | null>,
  open: boolean,
): Coords | null {
  const [coords, setCoords] = useState<Coords | null>(null);

  useEffect(() => {
    if (!open || !triggerRef.current) {
      setCoords(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const GAP = 8;

    let top = 0;
    let left = 0;

    switch (position) {
      case "top-center":
        top = rect.top + scrollY - GAP;
        left = rect.left + scrollX + rect.width / 2;
        break;
      case "top-start":
        top = rect.top + scrollY - GAP;
        left = rect.left + scrollX;
        break;
      case "top-end":
        top = rect.top + scrollY - GAP;
        left = rect.right + scrollX;
        break;
      case "bottom-center":
        top = rect.bottom + scrollY + GAP;
        left = rect.left + scrollX + rect.width / 2;
        break;
      case "bottom-start":
        top = rect.bottom + scrollY + GAP;
        left = rect.left + scrollX;
        break;
      case "bottom-end":
        top = rect.bottom + scrollY + GAP;
        left = rect.right + scrollX;
        break;
      case "left-center":
        top = rect.top + scrollY + rect.height / 2;
        left = rect.left + scrollX - GAP;
        break;
      case "right-center":
        top = rect.top + scrollY + rect.height / 2;
        left = rect.right + scrollX + GAP;
        break;
    }

    setCoords({ top, left });
  }, [open, position, triggerRef]);

  return coords;
}

const anchorClasses: Record<Position, string> = {
  "top-center": "bottom-0 left-1/2 -translate-x-1/2",
  "top-start": "bottom-0 left-0",
  "top-end": "bottom-0 right-0",
  "bottom-center": "top-0 left-1/2 -translate-x-1/2",
  "bottom-start": "top-0 left-0",
  "bottom-end": "top-0 right-0",
  "left-center": "top-1/2 right-0 -translate-y-1/2",
  "right-center": "top-1/2 left-0 -translate-y-1/2",
};

type ContentProps = {
  children: React.ReactNode;
  className?: string;
  position?: Position;
  container?: HTMLElement;
};

const Content = ({ children, className, position: posProp }: ContentProps) => {
  const { open, position: ctxPosition, triggerRef } = useContext(TooltipCtx);
  const position = posProp ?? ctxPosition;
  const coords = useTooltipCoords(position, triggerRef, open);

  if (!open || !coords) return null;

  return createPortal(
    <div
      className="absolute z-[999999] pointer-events-none"
      style={{ top: coords.top, left: coords.left }}
    >
      <div
        className={classNames(
          "absolute",
          anchorClasses[position],
          "p-3 rounded-sm text-xs text-foreground bg-background whitespace-nowrap",
          "shadow-[0_6px_6px_-6px_rgba(0,0,0,0.16)] drop-shadow-[0_0_1px_rgba(0,0,0,0.4)]",
          className,
        )}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};

type ArrowProps = { className?: string };

const Arrow = ({ className }: ArrowProps) => (
  <div
    className={classNames(
      "relative top-[-7px] rotate-45 w-3 h-3 rounded-sm bg-background",
      className,
    )}
  />
);

const Tooltip = Object.assign(TooltipRoot, { Trigger, Content, Arrow });

export default Tooltip;
